<template>
  <div
    :class="{
      'align-right': alignRight,
    }"
  >
    <div
      v-if="columnId === 'name'"
      class="name"
    >
      {{ nameString || "–" }}
    </div>
    <template v-if="columnId === 'filial'">
      {{ filialString || "–" }}
    </template>
    <template v-if="columnId === 'posts'">
      {{ positionsString || "–" }}
    </template>
    <template v-if="columnId === 'paytypes'">
      {{ paymentTypesString || "–" }}
    </template>
    <template v-if="columnId === 'period'">
      {{ periodString || "–" }}
    </template>
    <component
      :is="entry.type === 'zone' ? 'a' : 'span'"
      v-if="columnId === 'address'"
      :href="entry.zone.url"
      target="_blank"
    >
      {{ addressString }}
    </component>
    <template v-if="columnId === 'coordinates'">
      {{ coordsString || "–" }}
    </template>
    <span
      v-if="columnId === 'distance'"
      :class="{
        'default-number': !entry.distance.value,
        'positive-number': entry.distance.value && entry.distance.change === 'up',
        'negative-number': entry.distance.value && entry.distance.change === 'down',
      }"
    >
      {{ distanceString }}
    </span>
    <span
      v-if="columnId === 'price'"
      :class="{
        'default-number': !entry.price.value,
        'positive-number': entry.distance.value && entry.price.change === 'up',
        'negative-number': entry.distance.value && entry.price.change === 'down',
      }"
    >
      {{ priceString }}
    </span>
  </div>
</template>

<script>
import moment from "moment";

export default {
  props: {
    entry: {
      type: Object,
      required: true,
    },
    column: {
      type: Object,
      required: true,
    },
    lists: {
      type: Object,
      default: () => ({
        filials: [],
        positions: [],
        paymentTypes: [],
      }),
    },
  },
  computed: {
    columnId () {
      return this.column.id;
    },
    alignRight () {
      return ["distance", "price"].includes(this.columnId);
    },
    nameString () {
      if (this.entry.type === "address") {
        return this.entry.address.name;
      }

      return this.entry.zone.name;
    },
    filialString () {
      const id = this.entry.filial;

      if (!id) {
        return null;
      }

      const filial = this.lists.filials.find(({ value }) => value === id);

      if (!filial) {
        return null;
      }

      return filial.label;
    },
    positionsString () {
      const ids = this.entry.positions;

      if (!Array.isArray(ids) || !ids.length) {
        return null;
      }

      const positions = this.lists.positions.filter(({ value }) => ids.includes(value));

      if (!positions.length) {
        return null;
      }

      return positions.map(({ label }) => label).join(", ");
    },
    paymentTypesString () {
      const ids = this.entry.paymentTypes;

      if (!Array.isArray(ids) || !ids.length) {
        return null;
      }

      const types = this.lists.paymentTypes.filter(({ value }) => ids.includes(value));

      if (!types.length) {
        return null;
      }

      return types.map(({ label }) => label).join(", ");
    },
    periodString () {
      const config = this.entry.period || {};

      if (!config.type) {
        return null;
      }

      const result = [];
      let divider = ", ";

      if (config.type === "week") {
        (config.week || []).forEach(day => result.push(moment(day, "e", true).format("dd")));
      }

      if (config.type === "month") {
        (config.month || []).forEach(day => result.push(`${day}-е`));
      }

      if (["year", "random"].includes(config.type)) {
        const list = config[config.type] || [];

        list.forEach((date) => {
          const momentDate = moment(date, "DD.MM.YYYY", true);
          
          const dateParts = ["D", "MMM"];

          if (config.type === "random") {
            dateParts.push("YYYY");
          }

          result.push(momentDate.format(dateParts.join(" ")));
        });
      }

      if (config.type === "range") {
        result.push(config.range.from, config.range.to);
        divider = " – ";
      }

      return result.join(divider);
    },
    addressString () {
      const { entry } = this;

      if (entry.type === "address") {
        const {
          city = null,
          street = null,
          home = null,
          housing = null,
        } = entry.address;

        return [
          city,
          street,
          home,
          housing,
        ].filter(Boolean).join(", ");
      }

      return entry.zone.url;
    },
    coordsString () {
      const coords = this.entry.coords || {};

      return Object.values(coords).filter(Boolean).join(", ");
    },
    distanceString () {
      const distance = this.entry.distance || {};

      if (!distance.value) {
        return 0;
      }

      return (distance.change === "down" ? "–" : "") + Number(distance.value).toLocaleString();
    },
    priceString () {
      const price = this.entry.price || {};

      if (!price.value) {
        return 0;
      }

      return (price.change === "down" ? "–" : "") + Number(price.value).toLocaleString();
    },
  },
  methods: {
    dumbCommasFixer (text) {
      text = String(text || "").split(",");

      text = text.filter(part => !!String(part).trim().length);
      
      return text.join(", ");
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/assets/styles/vars';

.align-right {
  text-align: right;

  @include onlyMobile {
    text-align: left;
  }
}

.name {
  min-width: 115px;
}

a {
  word-break: break-word;
}
</style>